import React, {useLayoutEffect} from "react";
import _ from "lodash";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5locales_it_IT from "@amcharts/amcharts5/locales/it_IT"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {getTooltip, getXYChart, addCursor, getRenderAxisX, getRenderAxisY, COLORS} from "./commons";


const getChartHigh = (n) => {
  switch (true) {
    case (n < 2):
      return 'h-24'
    case (n < 5):
      return 'h-48'
    case (n < 8):
      return 'h-72'
    case (n < 11):
      return 'h-96'
    case (n < 14):
      return 'h-128'
    default:
      return 'h-152'
  }
}


function KpiProductBarChart({id, data, intl, showPrice}) {

  useLayoutEffect(() => {

    const _data = data.sort((a, b) => showPrice
      ?  (b.amt_closed + b.amt_open + b.amt_planning + b.amt_design) - (a.amt_closed + a.amt_open + a.amt_planning + a.amt_design)
      :  (b.val_closed + b.val_open + b.val_planning + b.val_design) - (a.val_closed + a.val_open + a.val_planning + a.val_design)
    ).slice(0, 20)

    let root = am5.Root.new(id);
    root.setThemes([am5themes_Animated.new(root)])
    root.locale = am5locales_it_IT;
    root.numberFormatter.set("numberFormat", "#.0a");

    // Create chart
    let chart = getXYChart(root, {layout: root.verticalLayout})
    addCursor(root, chart)

    let legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
      })
    );

    // Create axes
    let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "art_des_cmpy",
      renderer: getRenderAxisY(root, false, true)
    }))
    // yAxis.renderer.grid.template.set("forceHidden", true)

    let yRenderer = yAxis.get("renderer");
    yRenderer.labels.template.setAll({
      fontSize: 11,
      fontWeight: "bold",
      paddingRight: 20,
    });
    yAxis.data.setAll(_data);

    let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
      calculateTotals: true,
      renderer: getRenderAxisX(root, false),
      extraMax: 0.1,
      min: 0
    }))
    xAxis.get("renderer").labels.template.set("forceHidden", true);

    // Add series
    function createSeries(field, showTotal) {
      const _field = showPrice ? `amt_${field}` : `val_${field}`
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: intl.formatMessage({id:field}),
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: _field,
        valueYField: 'base_um',
        stacked: true,
        categoryYField: "art_des_cmpy",
        fill: COLORS[field],
        maskBullets: true,
        minBulletDistance: 10,
        tooltip: getTooltip(root, showPrice ? `[bold]{categoryY}[/]\n{name} {valueX}€` : `[bold]{categoryY}[/]\n{name}: {valueX} {valueY}`)
      }))

      series.columns.template.setAll({
        tooltipY: am5.percent(90),
        fillOpacity: 0.7
      });

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: showPrice ? "{valueX}€" : "{valueX} {valueY}",
            fontSize: 9,
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true
          })
        });
      });

      series.columns.template.onPrivate("width", function(width, target) {
        am5.array.each(target.dataItem.bullets, function(bullet) {
          if (width > 40 || bullet.get("sprite").get("text").includes('valueXTotal')) {
            bullet.get("sprite").show();
          }
          else {
            bullet.get("sprite").hide();
          }
        });
      });

      if (showTotal) {
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            sprite: am5.Label.new(root, {
              text: showPrice ? "[bold]{valueXTotal}€[/]" : "[bold]{valueXTotal} {valueY}[/]",
              centerY: am5.p50,
              centerX: am5.p0,
              populateText: true
            })
          });
        });
      }

      legend.data.push(series);
      series.data.setAll(_data);
      series.appear(1000);

    }

    createSeries('planning')
    createSeries('design')
    createSeries('open')
    createSeries('closed', true)

    return () => {
      root.dispose()
    }
  }, [data, id, showPrice]) // eslint-disable-line react-hooks/exhaustive-deps

  return <div id={id} className={`w-full ${getChartHigh(data.length)}`}/>
}

export default React.memo(KpiProductBarChart, (props, nextProps) => {
  return _.isEqual(props.data, nextProps.data) && _.isEqual(props.showPrice, nextProps.showPrice)
})
